import React, { useState, useEffect } from "react";
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, CircularProgress, Autocomplete, TextField,
    Modal, Box, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TEAL, modalStyle } from "../SetupCatalogReport/constants";
import { BootstrapButton } from "../SetupCatalogReport/components/BootstrapButton";
import {
    ANY, MULTI_FIELDS, ATTRIBUTE_ROWS,
    makeListboxProps, withSelected,
} from "./CustomFilterHelpers";
 
const COLLAPSE_THRESHOLD = 5;
 
const loadingAdornment = (isLoading, existingAdornment) => (
    <>
        {isLoading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
        {existingAdornment}
    </>
);
 
export function AttributeTable({
    reportLineId, reportLine, loadingLine,
    activeFilter, reportLineSqlPreview, displayFilterNo, isDraftActive, loadingActiveDetail, disabled,
    form, setForm,
    categorySearch, principalSearch, brandSearch, productSearch,
    categoryValue, principalValue, brandValue, productValue,
    packsizeOptions, packtypeOptions, flavourOptions,
    segment_1Options, segment_2Options, segment_3Options,
    loadingAttributes, triggerAttributes,
    savingForm, onSave,
}) {
    const [brandExpanded, setBrandExpanded] = useState(false);
    const [productExpanded, setProductExpanded] = useState(false);
 
    useEffect(() => {
        setBrandExpanded(false);
        setProductExpanded(false);
    }, [form.category_master_id, displayFilterNo]);
 
    const renderCollapsibleTags = (expanded, setExpanded) => (value, getTagProps) => {
        if (value.length <= COLLAPSE_THRESHOLD || expanded) {
            return value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name || option.id} size="small" />
            ));
        }
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <Chip size="small" label={`${value.length} dipilih`} />
                <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                    sx={{ textTransform: "none", fontSize: 11, minWidth: "unset", p: "2px 6px" }}
                >
                    Lihat semua
                </Button>
            </div>
        );
    };
 
  const getDefaultLabel = (rowKey) => {
    const row = ATTRIBUTE_ROWS.find((r) => r.key === rowKey);
    return row?.isSegment ? "NA" : "Any";
};

const renderCodeValue = (rowKey) => {
    if (MULTI_FIELDS.includes(rowKey)) {
        const arr = form[rowKey];
        if (!arr || arr.length === 0) return "Any";
        return arr.map((code) => (
            <div key={code} style={{ lineHeight: 1.6 }}>{code}</div>
        ));
    }
    return form[rowKey] || getDefaultLabel(rowKey);
};
 
    const renderDescriptionInput = (rowKey) => {
        if (rowKey === "category_master_id") {
            return (
                <Autocomplete
                    size="small"
                    disabled={disabled}
                    loading={categorySearch.loading}
                    loadingText="Memuat category..."
                    options={withSelected(categorySearch.options, categoryValue)}
                    filterOptions={(x) => x}
                    openOnFocus
                    getOptionLabel={(o) => o?.name || ""}
                    value={categoryValue}
                    ListboxProps={makeListboxProps(categorySearch)}
                    onInputChange={categorySearch.onInputChange}
                    onChange={(_, v) => setForm({
                        category_master_id: v?.id ? String(v.id) : "",
                        principal_master_id: "",
                        brand_master_ids: [],
                        product_codes: [],
                        packsize: "",
                        packtype: "",
                        flavour: "",
                        segment_1: "",
                        segment_2: "",
                        segment_3: "",
                    })}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText="Ketik untuk mencari category"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Cari category"
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                endAdornment: loadingAdornment(categorySearch.loading, params.InputProps.endAdornment),
                            }}
                            sx={{ px: 1 }}
                        />
                    )}
                />
            );
        }
 
        if (rowKey === "principal_master_id") {
            const opts = withSelected([ANY, ...principalSearch.options], principalValue);
            const showLoadingHint = principalSearch.loading && principalSearch.options.length === 0;
            return (
                <Autocomplete
                    size="small"
                    loading={principalSearch.loading}
                    loadingText="Memuat principal..."
                    disabled={disabled || !form.category_master_id}
                    options={opts}
                    filterOptions={(x) => x}
                    openOnFocus
                    onOpen={principalSearch.notifyOpen}
                    onClose={principalSearch.notifyClose}
                    getOptionLabel={(o) => o?.name || ""}
                    value={principalValue}
                    ListboxProps={makeListboxProps(principalSearch)}
                    onInputChange={principalSearch.onInputChange}
                    onChange={(_, v) => setForm((p) => ({
                        ...p,
                        principal_master_id: v && v.id !== "any" ? String(v.id) : "",
                        brand_master_ids: [],
                    }))}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText={form.category_master_id ? "Ketik untuk mencari principal" : "Pilih category dulu"}
                    renderOption={(props, option) =>
                        option.id === "any" && showLoadingHint ? (
                            <li {...props} key="principal-any-loading">
                                <span style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                                    <span>Any</span>
                                    <CircularProgress size={12} sx={{ ml: "auto" }} />
                                </span>
                            </li>
                        ) : (
                            <li {...props} key={option.id}>{option.name}</li>
                        )
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={!form.category_master_id ? "Pilih category dulu" : "Any"}
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                endAdornment: loadingAdornment(principalSearch.loading, params.InputProps.endAdornment),
                            }}
                            sx={{ px: 1 }}
                        />
                    )}
                />
            );
        }
 
        if (rowKey === "brand_master_ids") {
            return (
                <Autocomplete
                    multiple
                    size="small"
                    loading={brandSearch.loading}
                    loadingText="Memuat brand..."
                    disabled={disabled || !form.category_master_id}
                    options={withSelected(brandSearch.options, brandValue)}
                    filterOptions={(x) => x}
                    openOnFocus
                    onOpen={brandSearch.notifyOpen}
                    onClose={brandSearch.notifyClose}
                    getOptionLabel={(o) => o?.name || ""}
                    value={brandValue}
                    renderTags={renderCollapsibleTags(brandExpanded, setBrandExpanded)}
                    ListboxProps={makeListboxProps(brandSearch)}
                    onInputChange={brandSearch.onInputChange}
                    onChange={(_, v) => setForm((p) => ({ ...p, brand_master_ids: v.map((o) => String(o.id)) }))}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText={form.category_master_id ? "Ketik nama brand untuk mencari" : "Pilih category dulu"}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={!form.category_master_id ? "Pilih category dulu" : (form.brand_master_ids.length ? "Cari brand lain" : "Ketik untuk cari brand (Any)")}
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                endAdornment: loadingAdornment(brandSearch.loading, params.InputProps.endAdornment),
                            }}
                            sx={{ px: 1 }}
                        />
                    )}
                />
            );
        }
 
        if (rowKey === "product_codes") {
            return (
                <Autocomplete
                    multiple
                    size="small"
                    loading={productSearch.loading}
                    loadingText="Memuat product..."
                    disabled={disabled || !form.category_master_id}
                    options={withSelected(productSearch.options, productValue)}
                    filterOptions={(x) => x}
                    openOnFocus
                    onOpen={productSearch.notifyOpen}
                    onClose={productSearch.notifyClose}
                    getOptionLabel={(o) => o?.name || String(o.id)}
                    value={productValue}
                    renderTags={renderCollapsibleTags(productExpanded, setProductExpanded)}
                    ListboxProps={makeListboxProps(productSearch)}
                    onInputChange={productSearch.onInputChange}
                    onChange={(_, v) => setForm((p) => ({ ...p, product_codes: v.map((o) => String(o.id)) }))}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText={form.category_master_id ? "Ketik nama/kode produk untuk mencari" : "Pilih category dulu"}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={form.product_codes.length ? "Cari produk lain" : "Ketik untuk cari produk (Any)"}
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                endAdornment: loadingAdornment(productSearch.loading, params.InputProps.endAdornment),
                            }}
                            sx={{ px: 1 }}
                        />
                    )}
                />
            );
        }
 
   const optsMap = {
            packsize: packsizeOptions,
            packtype: packtypeOptions,
            flavour: flavourOptions,
            segment_1: segment_1Options,
            segment_2: segment_2Options,
            segment_3: segment_3Options,
        };

        const defaultLabel = getDefaultLabel(rowKey);

        const opts = loadingAttributes ? [] : [defaultLabel, ...(optsMap[rowKey] || [])];

        return (
            <Autocomplete
                size="small"
                disabled={disabled}
                loading={loadingAttributes}
                loadingText="Memuat opsi..."
                options={opts}
                value={form[rowKey] || defaultLabel}
                onOpen={triggerAttributes}
                onChange={(_, v) => setForm((p) => ({
                    ...p,
                    [rowKey]: v === defaultLabel ? "" : v || "",
                }))}
                noOptionsText={
                    loadingAttributes
                        ? "Memuat opsi..."
                        : form.category_master_id
                            ? "Tidak ada opsi"
                            : "Pilih category dulu"
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={defaultLabel}
                        variant="standard"
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: loadingAdornment(loadingAttributes, params.InputProps.endAdornment),
                        }}
                        sx={{ px: 1 }}
                    />
                )}
            />
        );
    };

    return (
        <Paper sx={{ flex: "1 1 620px", minWidth: 460, border: "1.5px solid rgb(234,243,248)", borderRadius: 2 }}>
            {disabled && (
                <div style={{ padding: "8px 16px", background: "#f5f5f5", fontSize: 12.5, color: "#777", borderBottom: "1px solid #eee" }}>
                    Belum ada custom filter — klik "Add Filter" untuk membuat yang pertama. Semua field di bawah menampilkan Any.
                </div>
            )}
            {!disabled && isDraftActive && (
                <div style={{ padding: "8px 16px", background: "#fff3e0", fontSize: 12.5, color: "#e65100", borderBottom: "1px solid #ffe0b2" }}>
                    Filter ini belum tersimpan — isi attribute di bawah lalu klik "Add".
                </div>
            )}
            <TableContainer>
                <Table size="small" sx={{ border: 1, borderColor: "grey.300", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13, width: 170 }}>report_line_id</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13, width: 90 }}>{reportLineId}</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13 }}>
                                {loadingLine ? <CircularProgress size={12} /> : (reportLine?.reportingline_desc?.trim() || "—")}
                            </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: "#e1f5ee" }}>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13 }}>custom filter no</TableCell>
                            <TableCell colSpan={2} sx={{ border: 1, borderColor: "grey.300", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                                {disabled ? "—" : (isDraftActive ? `${displayFilterNo} (belum tersimpan)` : displayFilterNo)}
                                {!disabled && loadingActiveDetail && <CircularProgress size={12} />}
                            </TableCell>
                        </TableRow>
 
                        <TableRow>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>Product Attributte</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>Code</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>Description</TableCell>
                        </TableRow>
 
                        {ATTRIBUTE_ROWS.map((row) => (
                            <TableRow key={row.key} sx={row.key === "category_master_id" ? { backgroundColor: "#fde4cf" } : {}}>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13 }}>
                                    {row.label}{row.required ? " *" : ""}
                                    {MULTI_FIELDS.includes(row.key) && (
                                        <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>(multiple)</span>
                                    )}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        border: 1, borderColor: "grey.300", fontSize: 13, color: "#444",
                                        width: 130, wordBreak: "break-all", whiteSpace: "normal",
                                    }}
                                >
                                    {renderCodeValue(row.key)}
                                </TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", p: 0.5 }}>
                                    {renderDescriptionInput(row.key)}
                                </TableCell>
                            </TableRow>
                        ))}
 
                        <TableRow sx={{ backgroundColor: "#e1f5ee" }}>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 13, fontWeight: 600, verticalAlign: "top" }}>
                                report_line_sql
                            </TableCell>
                            <TableCell colSpan={2} sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5, fontFamily: "monospace" }}>
                                {disabled ? "—" : reportLineSqlPreview}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
 
            {!disabled && (
                <div style={{ padding: 12, display: "flex", justifyContent: "flex-end", borderTop: "1px solid #f0f0f0" }}>
                    <BootstrapButton
                        disableRipple
                        disabled={savingForm}
                        onClick={onSave}
                        sx={{ py: 0.6, px: 2, fontSize: 12.5, color: "#fff" }}
                    >
                        {savingForm ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : (isDraftActive ? "Add" : "Update")}
                    </BootstrapButton>
                </div>
            )}
        </Paper>
    );
}
 
export function ProductsTable({
    disabled, isDraftActive, displayFilterNo, productTotalItems,
    loadingProducts, hasCategory, selectedProducts,
    productPage, productTotalPages, setProductPage,
}) {
    return (
        <Paper sx={{ border: "1.5px solid rgb(234,243,248)", borderRadius: 2 }}>
            <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                    Selected Product{!disabled && ` — by Filter No ${isDraftActive ? `${displayFilterNo} (draft)` : displayFilterNo}`}
                </span>
                <span style={{ fontSize: 12, color: "#bbb" }}>{productTotalItems} produk</span>
            </div>
            <TableContainer>
                <Table size="small" sx={{ border: 1, borderColor: "grey.300", borderCollapse: "collapse" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>product_code</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>category</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>principal</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>brand</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>product_name</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>packsize</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>packtype</TableCell>
                            <TableCell sx={{ border: 1, borderColor: "grey.300", fontWeight: 700, fontSize: 13 }}>flavour</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {disabled ? (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#bbb", border: 1, borderColor: "grey.300" }}>Belum ada custom filter — buat filter dulu untuk melihat produk.</TableCell></TableRow>
                        ) : loadingProducts ? (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, border: 1, borderColor: "grey.300" }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
                        ) : !hasCategory ? (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#bbb", border: 1, borderColor: "grey.300" }}>Pilih category dulu untuk melihat produk.</TableCell></TableRow>
                        ) : selectedProducts.length === 0 ? (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#bbb", border: 1, borderColor: "grey.300" }}>Tidak ada produk untuk kombinasi filter ini.</TableCell></TableRow>
                        ) : selectedProducts.map((p) => (
                            <TableRow key={p.id}>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.product_id}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.category_master}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.principal_master}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.brand_master}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.product_name}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.packsize}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.packtype}</TableCell>
                                <TableCell sx={{ border: 1, borderColor: "grey.300", fontSize: 12.5 }}>{p.flavour}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!disabled && !loadingProducts && hasCategory && selectedProducts.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "8px 16px", borderTop: "1px solid #f0f0f0" }}>
                    <Button size="small" disabled={productPage <= 1} onClick={() => setProductPage((p) => p - 1)} sx={{ textTransform: "none" }}>Prev</Button>
                    <span style={{ fontSize: 12.5 }}>Page {productPage} / {productTotalPages}</span>
                    <Button size="small" disabled={productPage >= productTotalPages} onClick={() => setProductPage((p) => p + 1)} sx={{ textTransform: "none" }}>Next</Button>
                </div>
            )}
        </Paper>
    );
}
 
export function RefreshModal({ open, onClose, loading, onConfirm }) {
    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ ...modalStyle, width: 420, p: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Refresh Custom Filter</h3>
                    <IconButton size="small" onClick={onClose} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <p style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
                    Ini akan mengambil ulang list product berdasarkan custom filter yang aktif.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={loading}
                        sx={{ textTransform: "none", color: "#999", borderColor: "#ddd" }}
                    >
                        Batal
                    </Button>
                    <BootstrapButton onClick={onConfirm} disabled={loading} sx={{ minWidth: 120, color: "#fff" }}>
                        {loading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Refresh"}
                    </BootstrapButton>
                </div>
            </Box>
        </Modal>
    );
}